import React, { useState, useCallback } from 'react';

const LeadsFilters = ({ 
  filters = {}, 
  onFiltersChange, 
  filterOptions = {}, 
  loading = false 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [isExpanded, setIsExpanded] = useState(false);

  // Debounced search
  const debouncedSearch = useCallback((value) => {
    const timeoutId = setTimeout(() => {
      setLocalFilters(prev => ({ ...prev, search: value }));
    }, 300);
    return () => clearTimeout(timeoutId);
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const cleanup = debouncedSearch(value);
    return cleanup;
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Handle range filter changes
  const handleRangeChange = (key, min, max) => {
    const newFilters = { 
      ...localFilters, 
      [`min_${key}`]: min || undefined,
      [`max_${key}`]: max || undefined
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Clear specific filter
  const clearFilter = (key) => {
    const newFilters = { ...localFilters };
    delete newFilters[key];
    delete newFilters[`min_${key}`];
    delete newFilters[`max_${key}`];
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    const newFilters = {};
    setLocalFilters(newFilters);
    setSearchTerm('');
    onFiltersChange(newFilters);
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    return Object.keys(localFilters).filter(key => 
      localFilters[key] !== undefined && 
      localFilters[key] !== '' && 
      localFilters[key] !== null
    ).length;
  };

  // Get filter chips
  const getFilterChips = () => {
    const chips = [];
    
    Object.entries(localFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        let label = '';
        let displayValue = value;
        
        switch (key) {
          case 'search':
            label = 'Search';
            displayValue = `"${value}"`;
            break;
          case 'state':
            label = 'State';
            break;
          case 'city':
            label = 'City';
            break;
          case 'access_tier':
            label = 'Access Tier';
            break;
          case 'active_installer_flag':
            label = 'Active Status';
            displayValue = value === 'true' ? 'Active' : 'Inactive';
            break;
          case 'min_installations':
            label = 'Min Installations';
            break;
          case 'max_installations':
            label = 'Max Installations';
            break;
          case 'min_cost_per_watt':
            label = 'Min Cost/Watt';
            break;
          case 'max_cost_per_watt':
            label = 'Max Cost/Watt';
            break;
          case 'min_risk_score':
            label = 'Min Risk Score';
            break;
          case 'max_risk_score':
            label = 'Max Risk Score';
            break;
          default:
            label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
        
        chips.push({ key, label, value: displayValue });
      }
    });
    
    return chips;
  };

  const styles = {
    container: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '24px',
      backdropFilter: 'blur(10px)',
      marginBottom: '24px',
      transition: 'all 0.3s ease',
      animation: 'slideInDown 0.5s ease forwards'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#ffffff',
      margin: 0
    },
    expandButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-1px)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    searchContainer: {
      marginBottom: '20px'
    },
    searchInput: {
      width: '100%',
      padding: '12px 16px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      outline: 'none',
      transition: 'all 0.2s ease',
      '&:focus': {
        borderColor: '#3B82F6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
        background: 'rgba(255, 255, 255, 0.15)'
      },
      '&::placeholder': {
        color: 'rgba(255, 255, 255, 0.5)'
      }
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '20px',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0.2s'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    filterLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: 'rgba(255, 255, 255, 0.8)',
      marginBottom: '4px'
    },
    select: {
      padding: '10px 12px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '6px',
      color: '#ffffff',
      fontSize: '14px',
      outline: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:focus': {
        borderColor: '#3B82F6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
        background: 'rgba(255, 255, 255, 0.15)'
      },
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)'
      }
    },
    rangeContainer: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    rangeInput: {
      flex: 1,
      padding: '10px 12px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '6px',
      color: '#ffffff',
      fontSize: '14px',
      outline: 'none',
      transition: 'all 0.2s ease',
      '&:focus': {
        borderColor: '#3B82F6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
        background: 'rgba(255, 255, 255, 0.15)'
      },
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)'
      }
    },
    rangeSeparator: {
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: '14px'
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    toggle: {
      position: 'relative',
      width: '44px',
      height: '24px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    toggleActive: {
      background: '#3B82F6'
    },
    toggleThumb: {
      position: 'absolute',
      top: '2px',
      left: '2px',
      width: '20px',
      height: '20px',
      background: '#ffffff',
      borderRadius: '50%',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
    },
    toggleThumbActive: {
      transform: 'translateX(20px)'
    },
    filterChips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '20px',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0.1s'
    },
    filterChip: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 12px',
      background: 'rgba(59, 130, 246, 0.2)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '16px',
      color: '#3B82F6',
      fontSize: '12px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      animation: 'scaleIn 0.3s ease forwards',
      '&:hover': {
        background: 'rgba(59, 130, 246, 0.3)',
        transform: 'scale(1.05)'
      }
    },
    chipRemove: {
      background: 'none',
      border: 'none',
      color: '#3B82F6',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '0',
      width: '16px',
      height: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      borderRadius: '50%',
      '&:hover': {
        background: 'rgba(59, 130, 246, 0.2)',
        transform: 'scale(1.2)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    clearAllButton: {
      padding: '6px 12px',
      background: 'rgba(239, 68, 68, 0.2)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '6px',
      color: '#EF4444',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(239, 68, 68, 0.3)',
        transform: 'translateY(-1px)'
      },
      '&:focus': {
        outline: '2px solid #EF4444',
        outlineOffset: '2px'
      }
    },
    loadingOverlay: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      color: 'rgba(255, 255, 255, 0.7)'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingOverlay}>
          <div>Loading filter options...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>
          Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
        </h3>
        <button
          style={styles.expandButton}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Collapse' : 'Expand'} Filters
          <span>{isExpanded ? '−' : '+'}</span>
        </button>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search by business name or license number..."
          value={searchTerm}
          onChange={handleSearchChange}
          style={styles.searchInput}
        />
      </div>

      {/* Filter Chips */}
      {getActiveFilterCount() > 0 && (
        <div style={styles.filterChips}>
          {getFilterChips().map(chip => (
            <div key={chip.key} style={styles.filterChip}>
              <span>{chip.label}: {chip.value}</span>
              <button
                style={styles.chipRemove}
                onClick={() => clearFilter(chip.key)}
                title="Remove filter"
              >
                ×
              </button>
            </div>
          ))}
          <button
            style={styles.clearAllButton}
            onClick={clearAllFilters}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Advanced Filters */}
      {isExpanded && (
        <div style={styles.filtersGrid}>
          {/* Location Filters */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>State</label>
            <select
              style={styles.select}
              value={localFilters.state || ''}
              onChange={(e) => handleFilterChange('state', e.target.value)}
            >
              <option value="">All States</option>
              {filterOptions.states?.map(state => (
                <option key={state.state} value={state.state}>
                  {state.state}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>City</label>
            <select
              style={styles.select}
              value={localFilters.city || ''}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            >
              <option value="">All Cities</option>
              {filterOptions.cities?.map(city => (
                <option key={city.city} value={city.city}>
                  {city.city}
                </option>
              ))}
            </select>
          </div>

          {/* Access Tier */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Access Tier</label>
            <select
              style={styles.select}
              value={localFilters.access_tier || ''}
              onChange={(e) => handleFilterChange('access_tier', e.target.value)}
            >
              <option value="">All Tiers</option>
              {filterOptions.accessTiers?.map(tier => (
                <option key={tier.access_tier} value={tier.access_tier}>
                  {tier.access_tier}
                </option>
              ))}
            </select>
          </div>

          {/* Active Status */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Active Status</label>
            <div style={styles.toggleContainer}>
              <div
                style={{
                  ...styles.toggle,
                  ...(localFilters.active_installer_flag === 'true' ? styles.toggleActive : {})
                }}
                onClick={() => handleFilterChange('active_installer_flag', 
                  localFilters.active_installer_flag === 'true' ? '' : 'true'
                )}
              >
                <div
                  style={{
                    ...styles.toggleThumb,
                    ...(localFilters.active_installer_flag === 'true' ? styles.toggleThumbActive : {})
                  }}
                />
              </div>
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                Active Installers Only
              </span>
            </div>
          </div>

          {/* Installations Range */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Total Installations</label>
            <div style={styles.rangeContainer}>
              <input
                type="number"
                placeholder="Min"
                value={localFilters.min_installations || ''}
                onChange={(e) => handleRangeChange('installations', e.target.value, localFilters.max_installations)}
                style={styles.rangeInput}
              />
              <span style={styles.rangeSeparator}>to</span>
              <input
                type="number"
                placeholder="Max"
                value={localFilters.max_installations || ''}
                onChange={(e) => handleRangeChange('installations', localFilters.min_installations, e.target.value)}
                style={styles.rangeInput}
              />
            </div>
          </div>

          {/* Cost per Watt Range */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Cost per Watt ($)</label>
            <div style={styles.rangeContainer}>
              <input
                type="number"
                step="0.01"
                placeholder="Min"
                value={localFilters.min_cost_per_watt || ''}
                onChange={(e) => handleRangeChange('cost_per_watt', e.target.value, localFilters.max_cost_per_watt)}
                style={styles.rangeInput}
              />
              <span style={styles.rangeSeparator}>to</span>
              <input
                type="number"
                step="0.01"
                placeholder="Max"
                value={localFilters.max_cost_per_watt || ''}
                onChange={(e) => handleRangeChange('cost_per_watt', localFilters.min_cost_per_watt, e.target.value)}
                style={styles.rangeInput}
              />
            </div>
          </div>

          {/* Risk Score Range */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Risk Score</label>
            <div style={styles.rangeContainer}>
              <input
                type="number"
                step="0.1"
                placeholder="Min"
                value={localFilters.min_risk_score || ''}
                onChange={(e) => handleRangeChange('risk_score', e.target.value, localFilters.max_risk_score)}
                style={styles.rangeInput}
              />
              <span style={styles.rangeSeparator}>to</span>
              <input
                type="number"
                step="0.1"
                placeholder="Max"
                value={localFilters.max_risk_score || ''}
                onChange={(e) => handleRangeChange('risk_score', localFilters.min_risk_score, e.target.value)}
                style={styles.rangeInput}
              />
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default LeadsFilters; 